import { useQuery } from '@tanstack/react-query';

import API from '@/config/api';

import {
	conversationDetailResponse,
	conversationEventsResponse,
	conversationsListResponse,
	type ConversationsParams,
	type UseConversation,
	type UseConversationEvents,
	type UseConversations,
} from './Conversations.schema';

const CONVERSATIONS_QUERY_KEY = ['conversations'];

export const conversationsKeys = {
	all: CONVERSATIONS_QUERY_KEY,
	lists: () => [...CONVERSATIONS_QUERY_KEY, 'list'] as const,
	list: (filters: ConversationsParams) => [...conversationsKeys.lists(), filters] as const,
	details: () => [...CONVERSATIONS_QUERY_KEY, 'detail'] as const,
	detail: (threadId: string) => [...conversationsKeys.details(), threadId] as const,
	events: (threadId: string) => [...conversationsKeys.details(), threadId, 'events'] as const,
};

export const useConversations = (params: ConversationsParams = {}, opts: UseConversations['opts'] = {}) => {
	const { page = 1, pageSize = 12, ...filters } = params;

	return useQuery<UseConversations['response']>({
		...opts,
		queryKey: conversationsKeys.list({ page, pageSize, ...filters }),
		queryFn: () =>
			API.get('/conversations', { params: { page, pageSize, ...filters } }).then((res) =>
				conversationsListResponse.parse(res.data),
			),
		staleTime: 15_000,
	});
};

export const useConversation = (threadId: string, opts: UseConversation['opts'] = {}) =>
	useQuery<UseConversation['response']>({
		...opts,
		queryKey: conversationsKeys.detail(threadId),
		queryFn: () => API.get(`/conversations/${threadId}`).then((res) => conversationDetailResponse.parse(res.data)),
		staleTime: 15_000,
		enabled: !!threadId,
	});

export const useConversationEvents = (threadId: string, opts: UseConversationEvents['opts'] = {}) =>
	useQuery<UseConversationEvents['response']>({
		...opts,
		queryKey: conversationsKeys.events(threadId),
		queryFn: () =>
			API.get(`/conversations/${threadId}/events`).then((res) => conversationEventsResponse.parse(res.data)),
		staleTime: 15_000,
		enabled: !!threadId,
	});
